import { sfxHit, sfxKill } from './audio-kit';
/* ============================================================
   密度層 —— 事件多到聽不清時，決定「發幾個音」
   ============================================================

   ## 為什麼需要這一層（2026-08-06 使用者回報「音效讓我很難受」）

   引擎的設計基準寫在 `audio-engine.ts`：「主砲每秒 3.75 發、命中每秒十幾次」。
   **實際遊戲差了一個數量級**：`.claude/notes/metrics-latest.md` 的種子 `33cd44`
   是 20 分鐘 215,499 擊殺 ＝ 每秒 180 隻。

   舊的擋法是 `audio.ts` 裡的時間節流（`gate('k',35)` → 28.6 音/秒、
   `gate('h',30)` → 33 音/秒）。它有兩個病：

   1. **密度還是太高。** 擊殺音本體 190 ms，28.6 音/秒代表**同時有 5.4 個**
      在響，而 thud 配方把它們全部壓在 38–150 Hz —— 那個窄頻段永遠有
      七八個音互相疊加，持續 20 分鐘。低頻堆積不會「刺」，會**悶到壓迫**。
      這就是「難受」的物理成因，而它跟音色無關 ——
      白噪版「太刺」、全低通版「太悶」，兩次都在移動頻率，病因一直沒被碰到。
   2. **回饋是錯的。** 節流是「時間到了就放行下一個」，被丟掉的就是丟掉了。
      你打死一片，聽到的是隨機被放行的那幾個 —— 音跟畫面對不上。

   ## 這一層的作法：聚合，不是丟棄

   時間窗（`WIN`）內的擊殺與命中**合併成一個音**，數量寫進 `n` 交給配方詮釋。
   一次殺十隻聽起來就該像「殺了一片」，而不是十個一模一樣的音搶同一個頻段。

   三條規則：

   1. **窗內有擊殺就只發擊殺**：擊殺是更強的回饋，命中音在同一個窗裡是冗餘的。
   2. **重要目標不聚合**：Boss、精英、當前鎖定目標立刻單獨發聲 ——
      玩家在意的是那一隻，它不該被雜兵稀釋掉。
   3. **位置取平均**：聚合音代表的是「周圍這一片」，平均位置正是那個語意。

   密度從 61 音/秒（28.6 擊殺 + 33 命中）降到最多 `1000/WIN` ≈ 14 音/秒。

   ## A/B

   `setDensity(false)` 切回舊的節流路徑（`audio.ts` 裡那條）。
   **沒有對照就證明不了新的比較好** —— 跟 v2/v1、thud/tonal 同一個原則。

   ## 決定性

   這一層只在渲染端跑，`Math.random()` 都沒有用到，`setTimeout` 不進模擬。
   headless 沒有 window，`sfxKill`／`sfxHit` 在引擎未就緒時自己早退。 */

/** 聚合窗（ms）。70 是「聽得出是一次事件」與「密度夠低」之間的折衷：
    再短會回到一片糊，再長會讓擊殺的回饋開始遲鈍（超過 100 ms 就感覺得到延遲）。 */
var WIN = 70;

/* headless（vitest／診斷腳本）一律關閉：那裡沒有 window，發不出聲音，
   而聚合器會排 `setTimeout` —— 測試結束時的 pending timer 是無謂的風險。 */
var on = typeof window !== 'undefined';
var kills = 0, hits = 0, sx = 0, sz = 0, cnt = 0;
var timer: any = null;

export function setDensity(v: boolean) { on = !!v; if (!on) reset(); return on; }
export function densityOn() { return on; }

function reset() {
  kills = 0; hits = 0; sx = 0; sz = 0; cnt = 0;
  if (timer) { clearTimeout(timer); timer = null; }
}

function arm() { if (!timer) timer = setTimeout(flush, WIN); }

function flush() {
  timer = null;
  if (!cnt) return;
  var x = sx / cnt, z = sz / cnt, n = kills;
  var anyHit = hits > 0;
  kills = 0; hits = 0; sx = 0; sz = 0; cnt = 0;
  /* 規則一：有擊殺就只發擊殺。命中音在同一個窗裡是冗餘的 ——
     那一片已經死了，「有沒有打到」不需要再講一次。 */
  if (n > 0) sfxKill({ x: x, z: z, n: n });
  else if (anyHit) sfxHit({ x: x, z: z });
}

/** 擊殺。`imp` = 這一隻值得單獨發聲（Boss／精英／當前鎖定目標）。 */
export function noteKill(x: number, z: number, imp?: boolean) {
  if (!on) return false;
  if (imp) { sfxKill({ x: x, z: z, n: 1, imp: true }); return true; }
  kills++; sx += x; sz += z; cnt++;
  arm();
  return true;
}

/** 命中。同上：重要目標立刻響，雜兵進聚合窗。 */
export function noteHit(x: number, z: number, imp?: boolean) {
  if (!on) return false;
  if (imp) { sfxHit({ x: x, z: z, imp: true }); return true; }
  hits++; sx += x; sz += z; cnt++;
  arm();
  return true;
}
